import { useCallback, useEffect, useMemo, useRef } from "react"
import type { SerializedDockview } from "dockview"
import type { OPDockviewPersistence } from "connected-components/op-layout-engine/types"
import { OK, LOADING } from "helpers/data-statuses"
import { useSaveContextPreferences, useReadContextPreference } from "hooks/preferences"

export function useDashboardOpLayoutEnginePersistence(): OPDockviewPersistence {
  const savePreferences = useSaveContextPreferences("dockviewLayout") as unknown as (value: SerializedDockview | null) => void
  const [storedLayout, status] = useReadContextPreference("dockviewLayout", null) as [SerializedDockview | null | undefined, string]

  const lastSavedRef = useRef<string | null>(null)
  const latestLayoutRef = useRef<SerializedDockview | null>(null)

  const layout = useMemo(() => {
    if (status === OK && storedLayout && typeof storedLayout === "object") {
      return storedLayout
    }
    return null
  }, [status, storedLayout])

  useEffect(() => {
    lastSavedRef.current = layout ? JSON.stringify(layout) : null
    latestLayoutRef.current = layout ?? null
  }, [layout])

  const persistState = useCallback((state: SerializedDockview | null) => {
    if (state === null) {
      lastSavedRef.current = null
      latestLayoutRef.current = null
      savePreferences(null)
      return
    }

    const serializedState = JSON.stringify(state)

    if (lastSavedRef.current === serializedState) {
      latestLayoutRef.current = state
      return
    }

    lastSavedRef.current = serializedState
    latestLayoutRef.current = state
    savePreferences(state)
  }, [savePreferences])

  return useMemo<OPDockviewPersistence>(() => ({
    load: () => latestLayoutRef.current ?? layout,
    save: (state: SerializedDockview) => {
      persistState(state)
    },
    clear: () => {
      persistState(null)
    },
    loading: status === LOADING,
  }), [layout, persistState, status])
}
